import { PERSONALIZATION } from '@/constants/network'
import { HOME_API_ENDPOINTS } from '@/constants/network/home'

import { isServer } from '.'

export const ENVIRONMENT = {
    DEVELOPMENT: 'development',
    STAGING: 'staging',
    BETA: 'beta',
    PRODUCTION: 'production'
}

const getQADomain = (hostname?: string) => {
    if (!isServer()) {
        return `https://${window.location.hostname}`
    }
    
    // If hostname is provided (from request headers), use it
    if (hostname) {
        if (hostname.includes('qa2.cleartrip.com')) {
            return 'https://qa2.cleartrip.com'
        }
        if (hostname.includes('qa2new.cleartrip.com')) {
            return 'https://qa2new.cleartrip.com'
        }
    }
    
    // Default to qa2new for backward compatibility
    return 'https://qa2new.cleartrip.com'
}

export const DOMAIN = {
    PROD: 'https://www.cleartrip.com',
    QA: getQADomain(),
    LOCAL: `http://localhost:${process.env.PORT || 3000}`,
    BETA: 'https://beta.cleartrip.com'
}

export const NON_DEV_ENVIRONMENT = [ENVIRONMENT.BETA, ENVIRONMENT.PRODUCTION]

export const NON_BETA_ENV_API_ENDPOINTS = [
    HOME_API_ENDPOINTS.HOME_WALLET_INFO,
    HOME_API_ENDPOINTS.HOME_GET_OTP,
    HOME_API_ENDPOINTS.VERIFY_OTP,
    HOME_API_ENDPOINTS.ACCOUNT_DETAILS,
    PERSONALIZATION
]

// Sentry environment configuration
export interface SentryEnvironmentConfig {
    environment: string;
    tracesSampleRate: number;
    debug: boolean;
    replaysOnErrorSampleRate: number;
    replaysSessionSampleRate: number;
    maskAllText: boolean;
    blockAllMedia: boolean;
    maxBreadcrumbs: number;
    maxSpans: number;
    sendDefaultPii: boolean;
    release?: string;
}

export function getSentryEnvironmentConfig(): SentryEnvironmentConfig {
    const environment = process.env.NEXT_PUBLIC_CT_ENV || ENVIRONMENT.DEVELOPMENT;
    
    // Get release from environment variables (prioritize client-side for browser, server-side for Node.js)
    const release = process.env.NEXT_PUBLIC_SENTRY_RELEASE || process.env.SENTRY_RELEASE;
    
    // Environment-specific defaults
    const configs: Record<string, SentryEnvironmentConfig> = {
        [ENVIRONMENT.DEVELOPMENT]: {
            environment: 'development',
            // Avoid burning spans in shared Sentry projects from local/dev traffic.
            // Override with SENTRY_TRACES_SAMPLE_RATE / NEXT_PUBLIC_SENTRY_TRACES_SAMPLE_RATE if needed.
            tracesSampleRate: 0.01,
            debug: true,
            replaysOnErrorSampleRate: 0,
            replaysSessionSampleRate: 0,
            maskAllText: false,
            blockAllMedia: false,
            maxBreadcrumbs: 100,
            maxSpans: 20,
            sendDefaultPii: false,
            release: release || 'development',
        },
        [ENVIRONMENT.STAGING]: {
            environment: 'staging',
            tracesSampleRate: 0.3,
            debug: false,
            replaysOnErrorSampleRate: 0,
            replaysSessionSampleRate: 0,
            maskAllText: true,
            blockAllMedia: true,
            maxBreadcrumbs: 50,
            maxSpans: 20,
            sendDefaultPii: false,
            release: release || 'unknown'
        },
        [ENVIRONMENT.BETA]: {
            environment: 'beta',
            tracesSampleRate: 0.3,
            debug: false,
            replaysOnErrorSampleRate: 0,
            replaysSessionSampleRate: 0,
            maskAllText: true,
            blockAllMedia: true,
            maxBreadcrumbs: 50,
            maxSpans: 20,
            sendDefaultPii: false,
            release: release || 'unknown'
        },
        [ENVIRONMENT.PRODUCTION]: {
            environment: 'production',
            tracesSampleRate: 0.05,
            debug: false,
            replaysOnErrorSampleRate: 0.1,
            replaysSessionSampleRate: 0.01,
            maskAllText: true,
            blockAllMedia: true,
            maxBreadcrumbs: 30,
            maxSpans: 20,
            sendDefaultPii: false,
            release: release || 'unknown'
        },
    };
    
    return configs[environment] ?? configs[ENVIRONMENT.DEVELOPMENT];
}
